import { type PortfolioSearchItem, SearchResultItem } from '@entities/portfolio';

import {
  GLOBAL_SEARCH_BLANK_HEIGHT_PX,
  GLOBAL_SEARCH_PANEL_PX,
} from './global-search-panel.constant';
import { GLOBAL_SEARCH_PANEL_TEXT as TEXT } from './global-search-panel.text';

export interface GlobalSearchPanelProps {
  /** 검색어가 2글자 미만이면 true — 사용 안내 3줄 */
  guide: boolean;
  status: 'default' | 'loading' | 'empty';
  /** "검색어/회사명" 으로 넣으면 company */
  scope: 'all' | 'company';
  searchText: string;
  searchCompany?: string;
  items: PortfolioSearchItem[];
  onSelect(item: PortfolioSearchItem): void;
  /** 패널 id(검색창의 aria-controls 가 가리킨다) */
  id?: string;
}

function Blank({ text }: { text: string }) {
  return (
    <p
      aria-live="polite"
      style={{ height: GLOBAL_SEARCH_BLANK_HEIGHT_PX }}
      className="flex items-center justify-center typo-body-medium text-fg-muted"
    >
      {text}
    </p>
  );
}

/** 헤더 검색 결과 패널: 안내(guide) · 검색 중 · 결과 · 결과 없음. 떠 있는 상자(꼭지 없음) */
export function GlobalSearchPanel({
  guide,
  status,
  scope,
  searchText,
  searchCompany,
  items,
  onSelect,
  id,
}: GlobalSearchPanelProps) {
  const showTitle = !guide && status !== 'loading';
  return (
    <div
      id={id}
      role="region"
      aria-label={TEXT.label}
      data-guide={guide}
      data-status={status}
      data-scope={scope}
      style={GLOBAL_SEARCH_PANEL_PX}
      className="scrollbar-none flex flex-col overflow-y-auto rounded-3xl border border-border-control bg-bg-surface p-2-5 shadow-halo"
    >
      {guide ? (
        TEXT.guide.map((line) => (
          <p key={line} className="py-2-5 whitespace-pre-line typo-body-medium text-fg-muted">
            {line}
          </p>
        ))
      ) : status === 'loading' ? (
        <Blank text={TEXT.loading} />
      ) : null}
      {showTitle ? (
        <p className="flex h-10 shrink-0 items-center justify-center gap-1">
          {scope === 'company' && searchCompany ? (
            <>
              <span className="typo-body-strong text-fg-default">{TEXT.quoted(searchCompany)}</span>
              <span className="typo-body-medium text-fg-muted">{TEXT.inCompany}</span>
            </>
          ) : null}
          <span className="typo-body-strong text-fg-default">{TEXT.quoted(searchText)}</span>
          <span className="typo-body-medium text-fg-muted">{TEXT.result}</span>
        </p>
      ) : null}
      {showTitle && status === 'empty' ? <Blank text={TEXT.empty} /> : null}
      {showTitle && status === 'default' ? (
        <ul role="list" className="flex flex-col">
          {items.map((item) => (
            <li key={item.idx}>
              <SearchResultItem item={item} searchText={searchText} onSelect={onSelect} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
